import { useEffect, useMemo, useRef, useState } from "react";
import { addYears, format } from "date-fns";
import useApiRequest from "../../../utils/hooks/useApiRequest";
import useFacility from "../../../utils/hooks/useFacility";
import { GET_SWIMMING_LESSONS_FIRST_TIME } from "../../api/apiRoutes";
import { SwimmingFilters } from "../../models/SwimmingScheduleData";

interface UseSwimmingFirstTimeOptions {
  enabled: boolean;
  filters: SwimmingFilters;
  saleStepExerciseGroupIds?: string;
  minDate?: Date;
  maxDate?: Date;
}

const parseFirstTimeResult = (result: unknown): Date | null => {
  if (result == null) return null;
  if (typeof result === "number") return new Date(result);
  if (typeof result === "string") {
    const parsed = new Date(result);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const buildExerciseGroupIds = (
  filters: SwimmingFilters,
  saleStepExerciseGroupIds?: string,
): string | undefined => {
  if (filters.exerciseGroups.length > 0) {
    return filters.exerciseGroups.join(";");
  }
  return saleStepExerciseGroupIds || undefined;
};

export default function useSwimmingFirstTime({
  enabled,
  filters,
  saleStepExerciseGroupIds,
  minDate,
  maxDate,
}: UseSwimmingFirstTimeOptions) {
  const facilityIds = useFacility();
  const { get } = useApiRequest();
  const isMountedRef = useRef(true);

  const [firstAvailableDate, setFirstAvailableDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);

  const exerciseGroupIds = useMemo(
    () => buildExerciseGroupIds(filters, saleStepExerciseGroupIds),
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.exerciseGroups, saleStepExerciseGroupIds],
  );

  const exerciseVisibility = filters.courseType !== "ALL" ? filters.courseType : undefined;

  const dateFromKey = useMemo(
    () => format(minDate ?? new Date(), "yyyy-MM-dd"),
    [minDate],
  );

  const dateToKey = useMemo(
    () => format(maxDate ?? addYears(new Date(), 1), "yyyy-MM-dd"),
    [maxDate],
  );

  useEffect(() => {
    if (!enabled || !facilityIds) {
      setFirstAvailableDate(null);
      setChecked(false);
      setLoading(false);
      return;
    }

    const fetchFirstTime = async () => {
      setLoading(true);
      setChecked(false);
      setFirstAvailableDate(null);

      const url = GET_SWIMMING_LESSONS_FIRST_TIME(
        facilityIds,
        dateFromKey,
        dateToKey,
        exerciseGroupIds,
        exerciseVisibility,
      );

      const { result, isError } = await get(url);

      if (!isMountedRef.current) return;

      setFirstAvailableDate(!isError ? parseFirstTimeResult(result) : null);
      setLoading(false);
      setChecked(true);
    };

    fetchFirstTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, facilityIds, exerciseGroupIds, exerciseVisibility, dateFromKey, dateToKey]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return { firstAvailableDate, loading, checked };
}
